/**
 * Supabase client.
 *
 * The anon key ships in the browser. That is safe here for exactly one reason:
 * row-level security is on for every table in `app.*`, so this key can only ever
 * read rows the signed-in user's membership allows. It is not a bypass.
 *
 * `platform.*` has RLS on with almost no policies, so this client cannot read it
 * at all except through the two narrow policies granted in the RLS migration.
 * Anything else that needs the control plane goes through an Edge Function with
 * the service role — never from here.
 */

import { createClient } from '@supabase/supabase-js';

const url = import.meta.env.VITE_SUPABASE_URL;
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

function missing(name: string): never {
  throw new Error(
    `${name} is not set. Copy .env.example to .env.local and fill it in — the app ` +
      `cannot talk to the database without it.`,
  );
}

if (!url) missing('VITE_SUPABASE_URL');
if (!anonKey) missing('VITE_SUPABASE_ANON_KEY');

// The default schema is `app`; `platform` is reached explicitly with
// `.schema('platform')`. The client's type is inferred so the schema
// parameter stays accurate rather than being widened to `public`.
export const supabase = createClient(url, anonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    // The estimator is at a vehicle, on a phone, for twenty minutes at a time.
    // Being signed out mid-job is the fastest way to lose their trust.
    detectSessionInUrl: true,
  },
  db: { schema: 'app' },
});
